import { useEffect, useState } from 'react';
import { db } from '@/lib/db';
import type { Suggestion, SuggestionCategory } from '@/lib/db/types';
import { Sheet } from '@/components/shared/Sheet';
import { Crest } from '@/components/shared/Crest';

interface Props {
  onClose: () => void;
}

const CATEGORIES: { id: SuggestionCategory; label: string }[] = [
  { id: 'idea', label: 'Idea' },
  { id: 'bug', label: 'Bug' },
  { id: 'feature', label: 'Feature' },
  { id: 'praise', label: 'Praise' },
  { id: 'complaint', label: 'Complaint' },
  { id: 'other', label: 'Other' },
];

const CATEGORY_LABELS = Object.fromEntries(CATEGORIES.map((c) => [c.id, c.label])) as Record<SuggestionCategory, string>;

export function SuggestionBoxSheet({ onClose }: Props) {
  const [text, setText] = useState('');
  const [category, setCategory] = useState<SuggestionCategory>('idea');
  const [sent, setSent] = useState(false);
  const [existing, setExisting] = useState<Suggestion[]>([]);

  useEffect(() => {
    void db.suggestions.list().then(setExisting).catch(() => {});
  }, []);

  const submit = async () => {
    if (!text.trim()) return;
    await db.suggestions.submit({ category, body: text.trim() });
    setText('');
    setSent(true);
    void db.suggestions.list().then(setExisting).catch(() => {});
    window.setTimeout(() => setSent(false), 2200);
  };

  return (
    <Sheet title="Suggestion Box" onClose={onClose}>
      <div className="px-4 pb-6 pt-2">
        <div className="relative mb-3.5 overflow-hidden rounded-[14px] bg-navy p-3.5 text-paper">
          <div className="pointer-events-none absolute -right-3.5 -top-3.5 opacity-[0.08]">
            <Crest size={90} variant="full" />
          </div>
          <div className="relative">
            <div className="text-[10px] font-bold tracking-[0.15em] text-gold">BUILD THE EAGLE WITH US</div>
            <div className="mt-1 font-serif text-base font-bold leading-tight">What should we build next?</div>
            <div className="mt-1 text-[11px] leading-snug text-white/65">
              Feature ideas, bugs, copy issues, course requests — anything that would make this better.
            </div>
          </div>
        </div>

        <div className="mb-2.5 flex flex-wrap gap-1.5">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCategory(c.id)}
              className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                category === c.id ? 'bg-navy text-cream' : 'border border-line bg-paper text-mute'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 500))}
          placeholder="It would be great if..."
          maxLength={500}
          rows={6}
          className="mb-2.5 w-full resize-y rounded-[10px] border border-line bg-paper p-3 text-sm text-ink outline-none"
        />
        <div className="mb-3.5 flex items-center justify-between">
          <div className="text-[10px] text-mute">{text.length} / 500</div>
          <button
            type="button"
            disabled={!text.trim()}
            onClick={() => void submit()}
            className={`rounded-[10px] px-5 py-2.5 text-[13px] font-bold tracking-wide ${
              text.trim() ? 'bg-gold text-navy' : 'bg-line text-mute opacity-50'
            }`}
          >
            {sent ? '✓ SENT' : 'SEND'}
          </button>
        </div>

        {existing.length > 0 && (
          <>
            <div className="mb-2 text-[10px] font-bold tracking-[0.15em] text-mute">YOUR PREVIOUS SUGGESTIONS</div>
            {existing.map((s) => (
              <div key={s.id} className="mb-1.5 rounded-[10px] border border-line bg-cream px-3 py-2.5">
                <div className="mb-1 text-[9px] font-bold uppercase tracking-wide text-gold-deep">
                  {CATEGORY_LABELS[s.category] || s.category}
                </div>
                <div className="text-xs leading-[1.5] text-ink-soft">&ldquo;{s.body}&rdquo;</div>
                <div className="mt-1 text-[9px] tracking-wide text-mute">
                  {new Date(s.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })} · {s.status}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </Sheet>
  );
}
